<script setup lang="ts">
import type { Placement } from '@floating-ui/vue';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/vue';
import { Icon } from '@iconify/vue';
/**
 * UiDatePicker - Design System Date Picker Component
 *
 * A date picker supporting single date and date range selection.
 *
 * @example
 * <UiDatePicker v-model="selectedDate" />
 * <UiDatePicker v-model="dateRange" mode="range" />
 */
import { computed, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue';

export interface DateRange {
   start: Date | null;
   end: Date | null;
}

export interface UiDatePickerProps {
   /** Selection mode */
   mode?: 'single' | 'range';
   /** Trigger size */
   size?: 'sm' | 'md';
   /** Popover placement relative to the trigger */
   placement?: Placement;
   /** Label for the input */
   label?: string;
   /** Placeholder text */
   placeholder?: string;
   /** Helper text */
   helperText?: string;
   /** Error message */
   errorMessage?: string;
   /** Minimum selectable date */
   minDate?: Date;
   /** Maximum selectable date */
   maxDate?: Date;
   /** Disabled state */
   disabled?: boolean;
   /** Date format for display */
   dateFormat?: string;
}

const props = withDefaults(defineProps<UiDatePickerProps>(), {
   mode: 'single',
   size: 'md',
   placement: 'bottom-start',
   placeholder: 'Select date',
   disabled: false,
   dateFormat: 'MMM D, YYYY',
});

const emit = defineEmits<{
   change: [value: Date | DateRange | null];
}>();

// Model can be Date for single, or DateRange for range mode
const modelValue = defineModel<Date | DateRange | null>();

// UI State
const isOpen = ref(false);
const pickerRef = useTemplateRef<HTMLDivElement>('pickerRef');
const triggerRef = useTemplateRef<HTMLButtonElement>('triggerRef');

// Floating UI: position the popover relative to the trigger.
//   - Caller picks the placement (defaults to bottom-start).
//   - flip() falls back if the chosen side is fully off-screen.
//   - shift() keeps the popover inside the viewport without changing the side.
const { floatingStyles } = useFloating(triggerRef, pickerRef, {
   placement: computed(() => props.placement),
   whileElementsMounted: autoUpdate,
   middleware: [
      offset(8),
      flip({ fallbackAxisSideDirection: 'start' }),
      shift({ padding: 8 }),
   ],
});

// Calendar state
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_NAMES_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const currentMonth = ref(new Date().getMonth());
const currentYear = ref(new Date().getFullYear());
const activePicker = ref<'start' | 'end'>('start');

// For range mode: separate month/year for end date
const endMonth = ref(new Date().getMonth());
const endYear = ref(new Date().getFullYear());

// Internal selection state
const selectedStartDate = ref<Date | null>(null);
const selectedEndDate = ref<Date | null>(null);

// Year options (current year down to 1900)
const yearOptions = computed(() => {
   const years: number[] = [];
   const maxYear = props.maxDate ? props.maxDate.getFullYear() : new Date().getFullYear() + 10;
   const minYear = props.minDate ? props.minDate.getFullYear() : 1900;
   for (let y = maxYear; y >= minYear; y--) {
      years.push(y);
   }
   return years;
});

// Days in the current month
const daysInMonth = computed(() => {
   const month = activePicker.value === 'start' ? currentMonth.value : endMonth.value;
   const year = activePicker.value === 'start' ? currentYear.value : endYear.value;
   return new Date(year, month + 1, 0).getDate();
});

// First day of month (0 = Sunday, 1 = Monday, etc.)
const firstDayOfMonth = computed(() => {
   const month = activePicker.value === 'start' ? currentMonth.value : endMonth.value;
   const year = activePicker.value === 'start' ? currentYear.value : endYear.value;
   return new Date(year, month, 1).getDay();
});

// Current picker month/year (for display)
const displayMonth = computed(() => activePicker.value === 'start' ? currentMonth.value : endMonth.value);
const displayYear = computed(() => activePicker.value === 'start' ? currentYear.value : endYear.value);

// Initialize from model value
watch(modelValue, (val) => {
   if (props.mode === 'single' && val instanceof Date) {
      selectedStartDate.value = val;
      currentMonth.value = val.getMonth();
      currentYear.value = val.getFullYear();
   }
   else if (props.mode === 'range' && val && typeof val === 'object' && 'start' in val) {
      const range = val as DateRange;
      selectedStartDate.value = range.start;
      selectedEndDate.value = range.end;
      if (range.start) {
         currentMonth.value = range.start.getMonth();
         currentYear.value = range.start.getFullYear();
      }
      if (range.end) {
         endMonth.value = range.end.getMonth();
         endYear.value = range.end.getFullYear();
      }
   }
}, { immediate: true });

// Format a date for display
function formatDate(date: Date | null): string {
   if (!date)
      return '';
   const month = MONTH_NAMES[date.getMonth()];
   return `${month} ${date.getDate()}, ${date.getFullYear()}`;
}

// Display value for the input
const displayValue = computed(() => {
   if (props.mode === 'single') {
      return selectedStartDate.value ? formatDate(selectedStartDate.value) : '';
   }
   if (selectedStartDate.value && selectedEndDate.value) {
      return `${formatDate(selectedStartDate.value)} - ${formatDate(selectedEndDate.value)}`;
   }
   if (selectedStartDate.value) {
      return `${formatDate(selectedStartDate.value)} - Select end date`;
   }
   return '';
});

// Check if a day is selected (for single mode or range endpoints)
function isSelected(day: number): boolean {
   const month = activePicker.value === 'start' ? currentMonth.value : endMonth.value;
   const year = activePicker.value === 'start' ? currentYear.value : endYear.value;
   const dateToCheck = new Date(year, month, day);

   if (selectedStartDate.value && isSameDay(dateToCheck, selectedStartDate.value))
      return true;
   if (selectedEndDate.value && isSameDay(dateToCheck, selectedEndDate.value))
      return true;
   return false;
}

// Check if a day is within the selected range
function isInRange(day: number): boolean {
   if (props.mode !== 'range' || !selectedStartDate.value || !selectedEndDate.value)
      return false;

   const month = activePicker.value === 'start' ? currentMonth.value : endMonth.value;
   const year = activePicker.value === 'start' ? currentYear.value : endYear.value;
   const dateToCheck = new Date(year, month, day);

   const start = selectedStartDate.value < selectedEndDate.value ? selectedStartDate.value : selectedEndDate.value;
   const end = selectedStartDate.value < selectedEndDate.value ? selectedEndDate.value : selectedStartDate.value;

   return dateToCheck > start && dateToCheck < end;
}

// Check if a day is disabled
function isDayDisabled(day: number): boolean {
   const month = activePicker.value === 'start' ? currentMonth.value : endMonth.value;
   const year = activePicker.value === 'start' ? currentYear.value : endYear.value;
   const date = new Date(year, month, day);

   if (props.minDate && date < props.minDate)
      return true;
   if (props.maxDate && date > props.maxDate)
      return true;
   return false;
}

function isSameDay(d1: Date, d2: Date): boolean {
   return d1.getDate() === d2.getDate()
      && d1.getMonth() === d2.getMonth()
      && d1.getFullYear() === d2.getFullYear();
}

// Handle day selection
function selectDay(day: number) {
   const month = activePicker.value === 'start' ? currentMonth.value : endMonth.value;
   const year = activePicker.value === 'start' ? currentYear.value : endYear.value;
   const selectedDate = new Date(year, month, day);

   if (props.mode === 'single') {
      selectedStartDate.value = selectedDate;
      modelValue.value = selectedDate;
      emit('change', selectedDate);
      isOpen.value = false;
   }
   else {
      if (activePicker.value === 'start') {
         selectedStartDate.value = selectedDate;
         activePicker.value = 'end';
         // Set end calendar to same or next month
         if (!selectedEndDate.value) {
            endMonth.value = month;
            endYear.value = year;
         }
      }
      else {
         selectedEndDate.value = selectedDate;
         // Swap if end is before start
         if (selectedStartDate.value && selectedDate < selectedStartDate.value) {
            const temp = selectedStartDate.value;
            selectedStartDate.value = selectedDate;
            selectedEndDate.value = temp;
         }
      }
   }
}

// Apply range selection
function applyRange() {
   if (selectedStartDate.value && selectedEndDate.value) {
      const range: DateRange = {
         start: selectedStartDate.value,
         end: selectedEndDate.value,
      };
      modelValue.value = range;
      emit('change', range);
      isOpen.value = false;
   }
}

// Clear selection
function clear() {
   selectedStartDate.value = null;
   selectedEndDate.value = null;
   modelValue.value = props.mode === 'single' ? null : { start: null, end: null };
   emit('change', modelValue.value);
}

// Navigate months
function prevMonth() {
   if (activePicker.value === 'start') {
      if (currentMonth.value === 0) {
         currentMonth.value = 11;
         currentYear.value--;
      }
      else {
         currentMonth.value--;
      }
   }
   else {
      if (endMonth.value === 0) {
         endMonth.value = 11;
         endYear.value--;
      }
      else {
         endMonth.value--;
      }
   }
}

function nextMonth() {
   if (activePicker.value === 'start') {
      if (currentMonth.value === 11) {
         currentMonth.value = 0;
         currentYear.value++;
      }
      else {
         currentMonth.value++;
      }
   }
   else {
      if (endMonth.value === 11) {
         endMonth.value = 0;
         endYear.value++;
      }
      else {
         endMonth.value++;
      }
   }
}

// Toggle picker
function togglePicker() {
   if (!props.disabled) {
      isOpen.value = !isOpen.value;
      if (isOpen.value) {
         activePicker.value = 'start';
      }
   }
}

// Close on outside click
function handleClickOutside(event: MouseEvent) {
   if (
      isOpen.value
      && pickerRef.value
      && triggerRef.value
      && !pickerRef.value.contains(event.target as Node)
      && !triggerRef.value.contains(event.target as Node)
   ) {
      isOpen.value = false;
   }
}

onMounted(() => {
   document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
   document.removeEventListener('click', handleClickOutside);
});

const hasError = computed(() => !!props.errorMessage);
</script>

<template>
   <div class="flex flex-col">
      <!-- Label -->
      <label v-if="label" class="block text-sm font-medium text-black mb-1.5">
         {{ label }}
      </label>

      <!-- Trigger Button -->
      <button
         ref="triggerRef"
         type="button"
         class="flex items-center w-full rounded-full transition-all duration-100
        border-0 ring-1 focus:ring-2 bg-white text-left"
         :class="[
            disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
            hasError ? 'ring-accent' : 'ring-primary',
            size === 'sm' ? 'h-9 px-3 gap-1.5 text-xs' : 'h-12 px-4 gap-2',
         ]"
         :disabled="disabled"
         @click="togglePicker"
      >
         <Icon
            icon="ph:calendar"
            class="text-neutral-dark shrink-0"
            :class="size === 'sm' ? 'size-4' : 'size-5'"
         />
         <span
            class="flex-1 truncate"
            :class="displayValue ? 'text-black' : 'text-neutral-dark'"
         >
            {{ displayValue || placeholder }}
         </span>
         <Icon
            v-if="displayValue && !disabled"
            icon="ph:x"
            class="text-neutral-dark hover:text-black"
            :class="size === 'sm' ? 'size-3.5' : 'size-4'"
            @click.stop="clear"
         />
      </button>

      <!-- Helper text -->
      <span v-if="helperText && !errorMessage" class="text-sm text-neutral-dark mt-1.5">
         {{ helperText }}
      </span>

      <!-- Error message -->
      <span v-if="errorMessage" class="text-sm text-accent mt-1.5" role="alert">
         {{ errorMessage }}
      </span>

      <!-- Picker Dropdown -->
      <Teleport to="body">
         <Transition
            enter-active-class="transition duration-150 ease-out"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition duration-100 ease-in"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
         >
            <div
               v-if="isOpen"
               ref="pickerRef"
               class="z-[9999] bg-white rounded-2xl shadow-xl ring-1 ring-black/10 p-4 w-80"
               :style="floatingStyles"
            >
               <!-- Range mode: From/To selector -->
               <div v-if="mode === 'range'" class="flex gap-2 mb-4">
                  <button
                     class="flex-1 px-3 py-2 rounded-lg text-left text-sm transition-colors"
                     :class="activePicker === 'start'
                        ? 'ring-2 ring-black bg-black/5'
                        : 'ring-1 ring-black/20 hover:ring-black/40'"
                     @click="activePicker = 'start'"
                  >
                     <div class="text-xs text-black/50 mb-0.5">
                        From
                     </div>
                     <div class="font-medium">
                        {{ selectedStartDate ? formatDate(selectedStartDate) : 'Select date' }}
                     </div>
                  </button>
                  <button
                     class="flex-1 px-3 py-2 rounded-lg text-left text-sm transition-colors"
                     :class="activePicker === 'end'
                        ? 'ring-2 ring-black bg-black/5'
                        : 'ring-1 ring-black/20 hover:ring-black/40'"
                     @click="activePicker = 'end'"
                  >
                     <div class="text-xs text-black/50 mb-0.5">
                        To
                     </div>
                     <div class="font-medium">
                        {{ selectedEndDate ? formatDate(selectedEndDate) : 'Select date' }}
                     </div>
                  </button>
               </div>

               <!-- Month/Year selector -->
               <div class="flex items-center justify-between mb-3">
                  <button
                     type="button"
                     class="p-1.5 hover:bg-neutral rounded-lg transition-colors"
                     @click="prevMonth"
                  >
                     <Icon icon="ph:caret-left" class="size-5" />
                  </button>

                  <div class="flex items-center gap-2">
                     <select
                        :value="displayMonth"
                        class="px-2 py-1 rounded-lg ring-1 ring-black/20 bg-white text-sm font-medium"
                        @change="activePicker === 'start'
                           ? currentMonth = Number(($event.target as HTMLSelectElement).value)
                           : endMonth = Number(($event.target as HTMLSelectElement).value)"
                     >
                        <option v-for="(name, idx) in MONTH_NAMES_FULL" :key="idx" :value="idx">
                           {{ name }}
                        </option>
                     </select>
                     <select
                        :value="displayYear"
                        class="px-2 py-1 rounded-lg ring-1 ring-black/20 bg-white text-sm font-medium"
                        @change="activePicker === 'start'
                           ? currentYear = Number(($event.target as HTMLSelectElement).value)
                           : endYear = Number(($event.target as HTMLSelectElement).value)"
                     >
                        <option v-for="y in yearOptions" :key="y" :value="y">
                           {{ y }}
                        </option>
                     </select>
                  </div>

                  <button
                     type="button"
                     class="p-1.5 hover:bg-neutral rounded-lg transition-colors"
                     @click="nextMonth"
                  >
                     <Icon icon="ph:caret-right" class="size-5" />
                  </button>
               </div>

               <!-- Day names -->
               <div class="grid grid-cols-7 text-center text-xs text-black/50 mb-2">
                  <span v-for="d in ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']" :key="d">{{ d }}</span>
               </div>

               <!-- Days grid — min-height keeps the popover stable across
                  months with fewer rows (5 vs 6 weeks) so Floating UI doesn't
                  reposition mid-interaction. -->
               <div class="grid grid-cols-7 text-center text-sm gap-y-1 min-h-[15rem]">
                  <!-- Empty cells for first day offset -->
                  <span v-for="n in firstDayOfMonth" :key="`pad-${n}`" />

                  <!-- Day buttons -->
                  <button
                     v-for="day in daysInMonth"
                     :key="day"
                     type="button"
                     class="mx-auto w-9 h-9 flex items-center justify-center transition-colors"
                     :class="[
                        isSelected(day)
                           ? 'bg-black text-white rounded-full'
                           : isInRange(day)
                              ? 'bg-primary/20 text-black'
                              : isDayDisabled(day)
                                 ? 'text-black/30 cursor-not-allowed'
                                 : 'hover:bg-neutral rounded-full',
                     ]"
                     :disabled="isDayDisabled(day)"
                     @click="selectDay(day)"
                  >
                     {{ day }}
                  </button>
               </div>

               <!-- Apply button for range mode -->
               <button
                  v-if="mode === 'range'"
                  type="button"
                  class="w-full mt-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  :class="selectedStartDate && selectedEndDate
                     ? 'bg-black text-white hover:bg-black/80'
                     : 'bg-neutral text-black/40 cursor-not-allowed'"
                  :disabled="!selectedStartDate || !selectedEndDate"
                  @click="applyRange"
               >
                  Apply
               </button>
            </div>
         </Transition>
      </Teleport>
   </div>
</template>
